/**
 * LaunchConfigPanel — admin-only editor for the Grand Opening target date
 * and the What's New ticker entries.
 *
 * Lives inside AdminPanel. No new route, no new top-level surface.
 *
 * Methodology hold: honest dates. If the launch slips, the admin updates the
 * field; the banner reflects truth. No hidden defaults, no silent drift.
 */
import { useState, useEffect } from 'react';
import { Calendar, Plus, Trash2, Pin, PinOff, RefreshCw } from 'lucide-react';
import { launchAPI } from '../lib/api';

function toDatetimeLocal(iso) {
  // Convert ISO → "YYYY-MM-DDTHH:mm" for <input type="datetime-local">
  if (!iso) return '';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    const off = d.getTimezoneOffset();
    const local = new Date(d.getTime() - off * 60_000);
    return local.toISOString().slice(0, 16);
  } catch { return ''; }
}

function fromDatetimeLocal(local) {
  // "YYYY-MM-DDTHH:mm" → full ISO with timezone
  if (!local) return null;
  return new Date(local).toISOString();
}

function ConfigEditor({ status, onSaved }) {
  const [target, setTarget] = useState(toDatetimeLocal(status?.kickstarter_open_at));
  const [expiryDays, setExpiryDays] = useState(status?.pass_expiry_days || 7);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => {
    setTarget(toDatetimeLocal(status?.kickstarter_open_at));
    setExpiryDays(status?.pass_expiry_days || 7);
  }, [status]);

  const handleSave = async () => {
    setSaving(true);
    setMsg('');
    try {
      const iso = fromDatetimeLocal(target);
      if (!iso) throw new Error('Pick a target date.');
      await launchAPI.adminSetConfig({
        kickstarter_open_at: iso,
        pass_expiry_days: Number(expiryDays) || 7,
      });
      setMsg('Saved. Banner updated.');
      onSaved?.();
    } catch (e) {
      setMsg(e?.response?.data?.detail || e.message || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="glass-panel p-4 space-y-3" data-testid="launch-config-editor">
      <h4 className="font-hud text-[#2F8A92] text-[11px] tracking-[0.25em] uppercase flex items-center gap-2">
        <Calendar size={12} /> Grand Opening Target
      </h4>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <label className="block">
          <span className="font-mono text-[10px] text-white/60 uppercase tracking-wider">Open Date / Time (local)</span>
          <input
            type="datetime-local"
            value={target}
            onChange={(e) => setTarget(e.target.value)}
            data-testid="launch-config-target-input"
            className="w-full mt-1 bg-black/40 border border-[#2F8A92]/20 px-3 py-2 font-mono text-sm text-white focus:outline-none focus:border-[#D4A84A]"
          />
        </label>
        <label className="block">
          <span className="font-mono text-[10px] text-white/60 uppercase tracking-wider">Pass Expiry (days after open)</span>
          <input
            type="number"
            min="1"
            max="30"
            value={expiryDays}
            onChange={(e) => setExpiryDays(e.target.value)}
            data-testid="launch-config-expiry-input"
            className="w-full mt-1 bg-black/40 border border-[#2F8A92]/20 px-3 py-2 font-mono text-sm text-white focus:outline-none focus:border-[#D4A84A]"
          />
        </label>
      </div>
      <div className="flex items-center gap-3">
        <button
          onClick={handleSave}
          disabled={saving}
          data-testid="launch-config-save-btn"
          className="bg-[#D4A84A] text-black font-hud text-xs tracking-widest uppercase px-4 py-2 hover:bg-[#D4A84A]/90 disabled:opacity-50"
        >
          {saving ? 'SAVING...' : 'SAVE TARGET'}
        </button>
        {msg && <span className="font-mono text-[11px] text-white/70" data-testid="launch-config-msg">{msg}</span>}
      </div>
      <p className="font-mono text-[10px] text-white/40 italic">
        Honest-date principle: if the launch slips, update this field. Banner reflects truth.
      </p>
    </div>
  );
}

function EntriesEditor({ entries, onChanged }) {
  const [draft, setDraft] = useState({ title: '', description: '', entry_date: new Date().toISOString().slice(0, 10), pinned: false });
  const [busyId, setBusyId] = useState(null);
  const [adding, setAdding] = useState(false);
  const [err, setErr] = useState('');

  const handleAdd = async () => {
    setAdding(true);
    setErr('');
    try {
      if (!draft.title.trim()) throw new Error('Title required.');
      await launchAPI.adminCreateEntry({
        title: draft.title.trim(),
        description: draft.description.trim(),
        entry_date: draft.entry_date,
        pinned: !!draft.pinned,
      });
      setDraft({ title: '', description: '', entry_date: new Date().toISOString().slice(0, 10), pinned: false });
      onChanged?.();
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || 'Add failed.');
    } finally {
      setAdding(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this ticker entry?')) return;
    setBusyId(id);
    try {
      await launchAPI.adminDeleteEntry(id);
      onChanged?.();
    } finally {
      setBusyId(null);
    }
  };

  const handleTogglePin = async (entry) => {
    setBusyId(entry.id);
    try {
      await launchAPI.adminUpdateEntry(entry.id, { ...entry, pinned: !entry.pinned });
      onChanged?.();
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="glass-panel p-4 space-y-3" data-testid="launch-entries-editor">
      <h4 className="font-hud text-[#2F8A92] text-[11px] tracking-[0.25em] uppercase flex items-center gap-2">
        <Plus size={12} /> What's New Entries
      </h4>

      {/* New entry form */}
      <div className="border border-[#D4A84A]/20 p-3 space-y-2">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
          <input
            type="date"
            value={draft.entry_date}
            onChange={(e) => setDraft({ ...draft, entry_date: e.target.value })}
            data-testid="launch-entry-date-input"
            className="bg-black/40 border border-[#2F8A92]/20 px-3 py-2 font-mono text-sm text-white focus:outline-none focus:border-[#D4A84A]"
          />
          <input
            type="text"
            placeholder="Title (e.g., Library of Alexandria chain published)"
            value={draft.title}
            onChange={(e) => setDraft({ ...draft, title: e.target.value })}
            maxLength={80}
            data-testid="launch-entry-title-input"
            className="md:col-span-2 bg-black/40 border border-[#2F8A92]/20 px-3 py-2 font-mono text-sm text-white focus:outline-none focus:border-[#D4A84A]"
          />
        </div>
        <textarea
          placeholder="Optional one-line description"
          value={draft.description}
          onChange={(e) => setDraft({ ...draft, description: e.target.value })}
          maxLength={240}
          rows={2}
          data-testid="launch-entry-description-input"
          className="w-full bg-black/40 border border-[#2F8A92]/20 px-3 py-2 font-mono text-xs text-white focus:outline-none focus:border-[#D4A84A]"
        />
        <div className="flex items-center justify-between">
          <label className="flex items-center gap-2 font-mono text-[11px] text-white/70 cursor-pointer">
            <input
              type="checkbox"
              checked={draft.pinned}
              onChange={(e) => setDraft({ ...draft, pinned: e.target.checked })}
              data-testid="launch-entry-pinned-input"
            />
            Pin to top
          </label>
          <button
            onClick={handleAdd}
            disabled={adding || !draft.title.trim()}
            data-testid="launch-entry-add-btn"
            className="bg-[#2F8A92] text-black font-hud text-[11px] tracking-widest uppercase px-3 py-1.5 hover:bg-[#2F8A92]/90 disabled:opacity-50"
          >
            {adding ? 'ADDING...' : 'ADD ENTRY'}
          </button>
        </div>
        {err && <p className="font-mono text-[11px] text-red-400">{err}</p>}
      </div>

      {/* Existing entries */}
      <ul className="space-y-2" data-testid="launch-entries-list">
        {entries.length === 0 && (
          <li className="font-mono text-[11px] text-white/40 italic">No entries yet. Add the first one above.</li>
        )}
        {entries.map((entry) => (
          <li
            key={entry.id}
            className="flex items-start gap-3 border border-white/5 p-2 group"
            data-testid={`launch-entry-${entry.id}`}
          >
            <span className="font-hud text-[10px] text-[#D4A84A] tracking-wider flex-shrink-0 mt-0.5 min-w-[60px]">
              {entry.entry_date}
            </span>
            <div className="min-w-0 flex-1">
              <div className="font-mono text-xs text-white">
                {entry.pinned && <Pin size={10} className="inline mr-1 text-[#D4A84A]" />}
                {entry.title}
              </div>
              {entry.description && (
                <p className="font-mono text-[11px] text-white/60 mt-0.5">{entry.description}</p>
              )}
            </div>
            <div className="flex items-center gap-1 flex-shrink-0">
              <button
                onClick={() => handleTogglePin(entry)}
                disabled={busyId === entry.id}
                data-testid={`launch-entry-pin-${entry.id}`}
                className="text-white/40 hover:text-[#D4A84A] p-1"
                title={entry.pinned ? 'Unpin' : 'Pin'}
              >
                {entry.pinned ? <PinOff size={12} /> : <Pin size={12} />}
              </button>
              <button
                onClick={() => handleDelete(entry.id)}
                disabled={busyId === entry.id}
                data-testid={`launch-entry-delete-${entry.id}`}
                className="text-white/40 hover:text-red-400 p-1"
                title="Delete"
              >
                <Trash2 size={12} />
              </button>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function LaunchConfigPanel() {
  const [status, setStatus] = useState(null);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    try {
      const [s, e] = await Promise.all([launchAPI.status(), launchAPI.adminListEntries()]);
      setStatus(s.data);
      setEntries(e.data || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  if (loading) {
    return (
      <div className="font-hud text-[#2F8A92] text-sm tracking-wider animate-pulse" data-testid="launch-config-loading">
        LOADING LAUNCH CONFIG...
      </div>
    );
  }

  const phaseLabel = status?.phase === 'countdown'
    ? 'PRE-LAUNCH (countdown active)'
    : status?.phase === 'live_week'
      ? 'KICKSTARTER LIVE (opening week)'
      : 'RETIRED (banner + ticker hidden)';

  return (
    <div className="space-y-4" data-testid="launch-config-panel">
      <div className="flex items-center justify-between">
        <h3 className="font-hud text-[#2F8A92] text-sm tracking-widest uppercase">
          Launch Banner & Ticker
        </h3>
        <button
          onClick={refresh}
          data-testid="launch-config-refresh"
          className="text-white/40 hover:text-[#2F8A92] p-1"
          title="Refresh"
        >
          <RefreshCw size={12} />
        </button>
      </div>
      <p className="font-hud text-[10px] text-white/60 tracking-wider">
        STATUS: <span className="text-[#D4A84A]">{phaseLabel}</span>
      </p>

      <ConfigEditor status={status} onSaved={refresh} />
      <EntriesEditor entries={entries} onChanged={refresh} />
    </div>
  );
}
